import { useState, useEffect, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import ItemList from '@/components/content/item-list';
import ItemGrid from '@/components/content/item-grid';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import ContentDrawer from './content-drawer';
import ShareContent from './share-content';
import api from '@/utils/api';
import { Content, Favorite, FieldOption } from '@/lib/types';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

interface Props {
  contents: Content[];
  workspaceId: string;
  contentTypeId: string;
  viewMode: 'list' | 'grid';
  viewFavorites: boolean;
  filterConfig?: FilterConfig[];
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  reloadContents: () => void;
}

export default function ContentList({
  contents,
  workspaceId,
  contentTypeId,
  viewMode,
  viewFavorites,
  filterConfig,
  lastContentElementRef,
  reloadContents,
}: Props) {
  const currentUser = useIsCurrentUser();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shareContentModal, setShareContentModal] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(null);
  const [selectedContent, setSelectedContent] = useState<Content | undefined>();
  const [autoOpenPrintDialog, setAutoOpenPrintDialog] = useState(false);
  const [favorites, setFavorites] = useState<{ [key: number]: Favorite }>({});
  const [isLoading, setIsLoading] = useState(false);

  const supportDrawerModal = useModalToggle();
  const navigate = useNavigate();

  const [deletedContentIds, setDeletedContentIds] = useState<number[]>(() => {
    const stored = localStorage.getItem('deletedContentIds');
    return stored ? JSON.parse(stored) : [];
  });
  const filteredContents = contents.filter((content) => !deletedContentIds.includes(content.id));

  const fetchFavorites = useCallback(async () => {
    if (!currentUser || !currentUser.id) {
      return;
    }
    const response = await api.get(`favorites/list/${currentUser.id}`);
    const favoritesArray = response.data.data || [];
    const favoritesMap = new Map(
      favoritesArray.map((fav: Favorite) => [fav.workspace_content_id, fav]),
    );
    setFavorites(Object.fromEntries(favoritesMap));
  }, [currentUser]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  useEffect(() => {
    localStorage.setItem('deletedContentIds', JSON.stringify(deletedContentIds));

    // Cleanup old deleted IDs after 1 hour to prevent localStorage bloat
    const now = Date.now();
    const storedTimestamps = JSON.parse(localStorage.getItem('deletedContentTimestamps') || '{}');

    const updatedTimestamps = { ...storedTimestamps };
    deletedContentIds.forEach((id) => {
      if (!storedTimestamps[id]) {
        updatedTimestamps[id] = now;
      }
    });

    localStorage.setItem('deletedContentTimestamps', JSON.stringify(updatedTimestamps));

    // Clean up IDs older than 1 hour
    const oneHourAgo = now - 60 * 60 * 1000;
    const idsToKeep = Object.entries(updatedTimestamps as Record<string, number>).reduce(
      (acc, [id, timestamp]) => (timestamp >= oneHourAgo ? [...acc, Number(id)] : acc),
      [] as number[],
    );

    if (idsToKeep.length !== deletedContentIds.length) {
      setDeletedContentIds(idsToKeep);
    }
  }, [deletedContentIds]);

  async function openContentDrawer(item: Content, autoPrint: boolean) {
    try {
      const response = await api.get(`public/content/${item.reference_id}`);
      const content = await response.data.data;
      if (!content) {
        return;
      }
      setSelectedContent(content);
      setAutoOpenPrintDialog(autoPrint);
      supportDrawerModal.open();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    }
  }

  async function handleView(item: Content) {
    await openContentDrawer(item, false);
  }

  async function handlePrint(item: Content) {
    await openContentDrawer(item, true);
  }

  function handleEdit(content: Content) {
    navigate(`/${workspaceId}/content-types/${contentTypeId}/contents/${content.id}/edit`);
  }

  function handleDelete(content: Content) {
    setSelectedContentId(content.id);
    setShowDeleteModal(true);
  }

  async function onDelete() {
    setIsLoading(true);
    try {
      await api.delete(`contents/${selectedContentId}`);

      // Add the deleted ID to our tracking array
      if (selectedContentId) {
        setDeletedContentIds((prev) =>
          prev.includes(selectedContentId) ? prev : [...prev, selectedContentId],
        );
      }

      reloadContents();
      setShowDeleteModal(false);
      setSelectedContentId(null);
      toaster.create({ title: 'Content removed', type: 'success' });
      setIsLoading(false);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete content',
      });
      setIsLoading(false);
    }
  }

  async function handleGeneratePublicLink(content: Content) {
    setShareContentModal(true);
    setSelectedContent(content);
  }

  async function handleFavourite(content: Content) {
    try {
      if (favorites[content.id]) {
        await api.delete(`favorites/${favorites[content.id].id}`);
        toaster.create({
          title: 'The content was removed to favorites',
          type: 'success',
        });
        fetchFavorites();
        return;
      }
      await api.post(`favorites`, {
        user_id: currentUser.id,
        workspace_content_id: content.id,
      });
      toaster.create({
        title: 'The content was added to favorites',
        type: 'success',
      });
      fetchFavorites();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to add to favorites',
      });
    }
  }

  async function handleDuplicate(content: Content) {
    try {
      const promise = api.post(`contents/duplicate/${content.id}`);

      toaster.promise(promise, {
        success: {
          title: 'The content was duplicated successfully',
        },
        error: {
          title: 'Failed to duplicate content',
        },
        loading: { title: 'Duplicating...', description: 'Please wait' },
      });
      reloadContents();
      await promise;

      setTimeout(() => {
        reloadContents();
      }, 2000);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to duplicate content',
      });
    }
  }

  async function handeChangeStatus(content: Content, status: 'draft' | 'published') {
    try {
      const promise = api.patch(`/contents/status/${content.id}/${status}`);

      toaster.promise(promise, {
        success: {
          title: `The content was ${status === 'published' ? 'shared to' : 'hidden from'} workspace`,
        },
        error: {
          title: `Failed to ${status === 'published' ? 'share to' : 'hide from'} workspace`,
        },
        loading: {
          title: `${status === 'published' ? 'Sharing to workspace' : 'Hiding from workspace'}...`,
          description: 'Please wait',
        },
      });

      setTimeout(() => {
        reloadContents();
      }, 1000);
      setTimeout(() => {
        reloadContents();
      }, 3000);
      await promise;
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change status',
      });
      setIsLoading(false);
    }
  }

  return (
    <Box>
      {viewMode === 'list' ? (
        <ItemList
          contents={filteredContents}
          favorites={favorites}
          viewMode={viewMode}
          viewFavorites={viewFavorites}
          filterConfig={filterConfig}
          lastContentElementRef={lastContentElementRef}
          onView={handleView}
          onFavourite={handleFavourite}
          onGeneratePiblicLink={handleGeneratePublicLink}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onChangeStatus={handeChangeStatus}
          onPrint={handlePrint}
        />
      ) : (
        <ItemGrid
          contents={contents}
          favorites={favorites}
          viewMode={viewMode}
          viewFavorites={viewFavorites}
          filterConfig={filterConfig}
          lastContentElementRef={lastContentElementRef}
          onView={handleView}
          onFavourite={handleFavourite}
          onGeneratePiblicLink={handleGeneratePublicLink}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onChangeStatus={handeChangeStatus}
          onPrint={handlePrint}
        />
      )}

      {/* Keep mounted; drive via isOpen. Unmounting a Chakra v3 dialog while it's
          open skips the close transition and leaves the body scroll/pointer lock
          behind, freezing the page. */}
      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => onDelete()}
        isLoading={isLoading}
        title={'Delete Content'}
        children={`You are about to delete content. It will also be removed from any practice plans and curriculums that use it. Press 'Confirm' to continue.`}
        confirmText={'Confirm'}
      />

      <ContentDrawer
        isOpen={supportDrawerModal.isOpen}
        onClose={supportDrawerModal.close}
        content={selectedContent}
        autoOpenPrintDialog={autoOpenPrintDialog}
      />

      {selectedContent && (
        <ShareContent
          isOpen={shareContentModal}
          onClose={() => setShareContentModal(false)}
          content={selectedContent}
          reloadContents={reloadContents}
        />
      )}
    </Box>
  );
}
